import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataCollection from '../../shared/DataCollection';
import LotCardList from './LotCardList.js';
import { useTranslation } from 'react-i18next';
import { daysSince, Fetch } from '../../utils/Utils.js';
import { formatDate as formatDateLocale } from '../../../utils/dateFormat.js';
import Patch from '../../utils/Patch.js';
import './ListLots.css';

const StatusDropdown = ({ selected, onChange }) => {
  const { t } = useTranslation();
  return (
    <select value={selected || 'active'} onChange={onChange}>
      <option value="active">{t('lot_status_active')}</option>
      <option value="archived">{t('lot_status_archived')}</option>
    </select>
  );
};

const formatLotId = (lotId, prefix) => {
  if (!lotId && lotId !== 0) return '-';
  return `${prefix}-${String(lotId).padStart(4, '0')}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const dateStr = dateString.length === 10 ? dateString + 'T12:00:00' : dateString;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateString;
  return formatDateLocale(date);
};

export const ListLots = () => {
  const { t }        = useTranslation();
  const navigate     = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [animalCounts, setAnimalCounts] = useState({});
  const [protocols, setProtocols] = useState([]);
  const [assignmentsByLotId, setAssignmentsByLotId] = useState({});
  const collection = 'lots';

  useEffect(() => {
    Fetch('api/data/lots.animals?search_fields=type&search_values=reproduction', 'get', null, (data) => {
      const counts = {};
      // Membership rows are never deleted (see lots.OnlyActive) — a
      // released/archived-cycle animal keeps its row with ended_at set, so
      // it must be excluded here or a lot's count never drops after
      // mark_lot_archived releases its members.
      (data || []).filter(animal => !animal.ended_at).forEach(animal => {
        if (animal.lot_id) {
          counts[animal.lot_id] = (counts[animal.lot_id] || 0) + 1;
        }
      });
      setAnimalCounts(counts);
      setRefreshKey(k => k + 1);
    });
    Fetch('api/data/lots.plans', 'get', null, (data) => setProtocols(data || []));
    // start_date/current_day live on each lot's assignment now, not cached
    // on the lot doc — one bulk fetch instead of one per row. Uses "latest"
    // rather than "active" so archived lots still show their final
    // start_date/day instead of "-"/"Invalid date provided" once their
    // assignment ends.
    Fetch('api/lots/assignments/latest', 'GET', null, (data) => {
      const byLotId = {};
      (data || []).forEach(a => { byLotId[a.lot_id] = a; });
      setAssignmentsByLotId(byLotId);
    });
  }, []);

  const getProtocolName = (protocolId) => {
    const p = protocols.find(p => p._id === protocolId || (p._id?.$oid) === protocolId);
    return p ? p.name : '-';
  };

  const handleStatusChange = (e, lotId) => {
    Patch('lots', lotId, 'status', e.target.value,
      () => setRefreshKey(k => k + 1),
      () => {}
    );
  };

  const columns = [
    {
      name: t("lot_id"),
      selector: row => row.lot_id,
      sortable: true,
      cell: row => (
        <span
          className="lot-link"
          onClick={() => navigate(`/reproduction/lots/${row.lot_id}`)}
        >
          {formatLotId(row.lot_id, t('lot_prefix'))}
        </span>
      ),
    },
    { name: t("name"),          selector: row => row.name || '',                   sortable: true },
    { name: t("protocol_name"), selector: row => getProtocolName(row.protocol_id), sortable: true },
    { name: t("start_date"),    selector: row => assignmentsByLotId[row.lot_id]?.start_date, sortable: true, cell: row => formatDate(assignmentsByLotId[row.lot_id]?.start_date) },
    { name: t("current_day"),   selector: row => daysSince(assignmentsByLotId[row.lot_id]?.start_date), sortable: true },
    { name: t("animals_count"), selector: row => animalCounts[row.lot_id] || 0, sortable: true },
    { name: t("created_by"),    selector: row => row.created_by, sortable: true },
    {
      name: t("status"),
      selector: row => row.status || 'active',
      sortable: true,
      cell: row => (
        <StatusDropdown
          selected={row.status}
          onChange={(e) => handleStatusChange(e, row._id)}
        />
      ),
    },
  ];

  return (
    <DataCollection
      key={refreshKey}
      collection={collection}
      searchFields={{ type: 'reproduction' }}
      columns={columns}
      sortField={1}
      mobileComponent={(data, onRefresh) => (
        <LotCardList
          data={data}
          onRefresh={onRefresh}
          animalCounts={animalCounts}
          assignmentsByLotId={assignmentsByLotId}
        />
      )}
    />
  );
}

export default ListLots;
